
import React from 'react';
import { useTranslation } from 'react-i18next';

const ProcessSection: React.FC = () => {
  const { t } = useTranslation();
  const processes = [
    {
      step: '01',
      title: t('process.items.1.title'),
      description: t('process.items.1.desc'),
      icon: 'ri-chat-3-line',
      details: t('process.items.1.details', { returnObjects: true }) as string[]
    },
    {
      step: '02',
      title: t('process.items.2.title'),
      description: t('process.items.2.desc'),
      icon: 'ri-draft-line',
      details: t('process.items.2.details', { returnObjects: true }) as string[]
    },
    {
      step: '03',
      title: t('process.items.3.title'),
      description: t('process.items.3.desc'),
      icon: 'ri-code-line',
      details: t('process.items.3.details', { returnObjects: true }) as string[]
    },
    {
      step: '04',
      title: t('process.items.4.title'),
      description: t('process.items.4.desc'),
      icon: 'ri-rocket-line',
      details: t('process.items.4.details', { returnObjects: true }) as string[]
    },
    {
      step: '05',
      title: t('process.items.5.title'),
      description: t('process.items.5.desc'),
      icon: 'ri-customer-service-2-line',
      details: t('process.items.5.details', { returnObjects: true }) as string[]
    }
  ];

  return (
    <section id="process" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t('process.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('process.intro')}
          </p>
        </div>

        {/* Desktop Process Flow */}
        <div className="hidden lg:block">
          <div className="relative">
            {/* Connection Line */}
            <div className="absolute top-20 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-blue-400 to-blue-200"></div>
            
            <div className="grid grid-cols-5 gap-8">
              {processes.map((process, index) => (
                <div key={index} className="relative">
                  {/* Step Circle */}
                  <div className="relative z-10 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <i className={`${process.icon} text-white text-2xl`}></i>
                  </div>
                  
                  {/* Step Number */}
                  <div className="absolute -top-3 -right-2 w-8 h-8 bg-white border-2 border-blue-600 rounded-full flex items-center justify-center z-20">
                    <span className="text-blue-600 font-bold text-sm">{process.step}</span>
                  </div>
                  
                  {/* Content */}
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {process.title}
                    </h3>
                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                      {process.description}
                    </p>
                    
                    {/* Details */}
                    <div className="space-y-2">
                      {process.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="flex items-center justify-center">
                          <div className="w-3 h-3 flex items-center justify-center mr-2">
                            <i className="ri-check-line text-green-500 text-xs"></i>
                          </div>
                          <span className="text-xs text-gray-600">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile Process Flow */}
        <div className="lg:hidden">
          <div className="space-y-8">
            {processes.map((process, index) => (
              <div key={index} className="relative">
                {/* Connection Line */}
                {index < processes.length - 1 && (
                  <div className="absolute left-8 top-16 w-0.5 h-16 bg-blue-200"></div>
                )}
                
                <div className="flex items-start">
                  {/* Step Circle */}
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                      <i className={`${process.icon} text-white text-2xl`}></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white border-2 border-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-bold text-sm">{process.step}</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="ml-6 flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {process.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {process.description}
                    </p>
                    
                    {/* Details */}
                    <div className="grid grid-cols-2 gap-2">
                      {process.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="flex items-center">
                          <div className="w-3 h-3 flex items-center justify-center mr-2">
                            <i className="ri-check-line text-green-500 text-xs"></i>
                          </div>
                          <span className="text-sm text-gray-600">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              准备开始您的项目了吗？
            </h3>
            <p className="text-xl mb-8 text-blue-100">
              联系我们，获取专业的技术咨询和项目报价
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2 inline-flex">
                  <i className="ri-phone-fill"></i>
                </div>
                立即咨询
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
